import { useState, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import mapPin from "../../assets/pin.png";
import {
  AirIco,
  BoxIco,
  ElevatorIco,
  ParkingIco,
  SunIco,
  SwimmingPool,
  TerraceIco,
  ArrowLeft,
  Rooms,
  Bathrooms,
  PlantOfInterest,
} from "../../components";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../../components/footer/footer.component";
import { normalizeAddress } from "../../config/helpers.config";
import {
  ExcentButton,
  ExcentIncrementButton,
  ExcentSubmitButton1,
  ExcentSubmitButton2,
} from "./activeProfiling.styles";

const MAPBOXTOKEN = process.env.REACT_APP_MAPBOXTOKEN;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_STREETVIEW_KEY;
const MAPBOX_STYLE = process.env.REACT_APP_MAPBOX_STYLE;

export const ActiveProfiling = () => {
  const location = useLocation();
  const mapContainer = useRef(null);
  const navigate = useNavigate();
  const map = useRef(null);
  // for context usage
  // const { assetData } = useCalculatorContext();
  mapboxgl.accessToken = MAPBOXTOKEN;

  const searchParams = new URLSearchParams(location.search);
  //  Save URL Parameter data
  const address = searchParams.get("address");
  const area = parseInt(searchParams.get("area"));
  const lon = parseFloat(searchParams.get("lon"));
  const lat = parseFloat(searchParams.get("lat"));
  const floor = parseInt(searchParams.get("floor"));
  const block = parseInt(searchParams.get("block"));
  const staircase = searchParams.get("staircase");
  const rooms = parseInt(searchParams.get("rooms"));
  const nbaths = parseInt(searchParams.get("nbaths"));
  const propertyType = parseInt(searchParams.get("property_type"));
  const has_garage =
    parseInt(searchParams.get("has_garage")) === 1 ? true : false;
  const has_pool = parseInt(searchParams.get("has_pool")) === 1 ? true : false;
  const has_air_conditioner =
    parseInt(searchParams.get("has_air_conditioner")) === 1 ? true : false;
  const has_elevator =
    parseInt(searchParams.get("has_elevator")) === 1 ? true : false;
  const has_storage =
    parseInt(searchParams.get("has_storage")) === 1 ? true : false;
  const is_exterior =
    parseInt(searchParams.get("is_exterior")) === 1 ? true : false;
  const has_terrace =
    parseInt(searchParams.get("has_terrace")) === 1 ? true : false;
  const cadastral_bi = searchParams.get("cadastral_bi");
  const boundary_id = parseInt(searchParams.get("boundary_id"));
  const municipio = searchParams.get("municipio");
  const provincia = searchParams.get("provincia");
  const postal_code = parseInt(searchParams.get("postal_code"));
  const iframeKey = searchParams.get("key");
  const qaTesting = searchParams.get("qa_testing");
  const build_status = parseInt(searchParams.get("build_status"));

  // Error states
  const [optionError, setOptionError] = useState("");
  const [areaError, setAreaError] = useState("");
  const [errorTypeOfProperty, setErrorTypeOfProperty] = useState("");

  // Data states
  const [areaSelected, setAreaSelected] = useState(90);
  const [buildStatus, setBuildStatus] = useState(
    build_status ? build_status : null
  );
  const [typeOfProperty, setTypeOfProperty] = useState(propertyType);
  const [floorNumber, setFloorNumber] = useState(
    Number.isNaN(floor) ? null : floor
  );
  const [isFloorFocused, setIsFloorFocused] = useState(false);
  const [roomsNumber, setRoomsNumber] = useState(
    Number.isNaN(rooms) ? null : rooms
  );
  const [isRoomsFocused, setIsRoomsFocused] = useState(false);
  const [bathroomNumber, setBathroomNumber] = useState(
    Number.isNaN(nbaths) ? null : nbaths
  );
  const [isBathroomsFocused, setIsBathroomsFocused] = useState(false);

  // Features states
  /* eslint-disable */
  const [hasGarage, setHasGarage] = useState(has_garage);
  const [hasAirConditioner, setHasAirConditioner] =
    useState(has_air_conditioner);
  const [hasPool, setHasPool] = useState(has_pool);
  const [isExterior, setIsExterior] = useState(is_exterior);
  const [hasStorage, setHasStorage] = useState(has_storage);
  const [hasTerrace, setHasTerrace] = useState(has_terrace);
  const [hasElevator, setHasElevator] = useState(has_elevator);
  /* eslint-enable */

  const resetFilters = () => {
    setBuildStatus(null);
    setAreaSelected(90);
    setTypeOfProperty("");
    setOptionError("");
    setAreaError("");
    setErrorTypeOfProperty("");
    setFloorNumber(0);
    setRoomsNumber(0);
    setBathroomNumber(0);
    uncheckAllFeatures();
    setTimeout(() => {
      setTypeOfProperty("");
      setAreaSelected(null);
    }, 0);
  };

  const typeOfPropertySelected =
    document.getElementById("TypeOfProperty")?.value;

  const decrementFloor = () => {
    setFloorNumber(floorNumber - 1);
  };
  const incrementFloor = () => {
    if (Number.isNaN(floorNumber)) {
      setFloorNumber(0);
    }
    setFloorNumber(floorNumber + 1);
  };

  const decrementRooms = () => {
    if (roomsNumber === 0) {
      return;
    }
    setRoomsNumber(roomsNumber - 1);
  };
  const incrementRooms = () => {
    if (Number.isNaN(roomsNumber)) {
      setRoomsNumber(0);
    }
    setRoomsNumber(roomsNumber + 1);
  };

  const decrementBathroom = () => {
    if (bathroomNumber === 0) {
      return;
    }
    setBathroomNumber(bathroomNumber - 1);
  };
  const incrementBathroom = () => {
    if (Number.isNaN(bathroomNumber)) {
      setBathroomNumber(0);
    }
    setBathroomNumber(bathroomNumber + 1);
  };

  const getIcon = (fill) => {
    return [
      {
        id: "is_exterior",
        icon: <SunIco fill={fill} />,
        checked: isExterior,
      },
      {
        id: "has_storage",
        icon: <BoxIco fill={fill} />,
        checked: hasStorage,
      },
      {
        id: "has_pool",
        icon: <SwimmingPool fill={fill} />,
        checked: hasPool,
      },
      {
        id: "has_air_conditioner",
        icon: <AirIco fill={fill} />,
        checked: hasAirConditioner,
      },
      {
        id: "has_garage",
        icon: <ParkingIco fill={fill} />,
        checked: hasGarage,
      },
      {
        id: "has_elevator",
        icon: <ElevatorIco fill={fill} />,
        checked: hasElevator,
      },
      {
        id: "has_terrace",
        icon: <TerraceIco fill={fill} />,
        checked: hasTerrace,
      },
    ];
  };

  const [checkboxes, setCheckboxes] = useState([
    {
      id: "is_exterior",
      label: "Exterior",
      icon: <SunIco fill={isExterior ? "#FFF" : "#3E3E3E"} />,
      checked: isExterior,
    },
    {
      id: "has_storage",
      label: "Trastero",
      icon: <BoxIco fill={hasStorage ? "#FFF" : "#3E3E3E"} />,
      checked: hasStorage,
    },
    {
      id: "has_pool",
      label: "Piscina",
      icon: <SwimmingPool fill={hasPool ? "#FFF" : "#3E3E3E"} />,
      checked: hasPool,
    },
    {
      id: "has_air_conditioner",
      label: "AC",
      icon: <AirIco fill={hasAirConditioner ? "#FFF" : "#3E3E3E"} />,
      checked: hasAirConditioner,
    },
    {
      id: "has_garage",
      label: "Garaje",
      icon: <ParkingIco fill={hasGarage ? "#FFF" : "#3E3E3E"} />,
      checked: hasGarage,
    },
    {
      id: "has_elevator",
      label: "Ascensor",
      icon: <ElevatorIco fill={hasElevator ? "#FFF" : "#3E3E3E"} />,
      checked: hasElevator,
    },
    {
      id: "has_terrace",
      label: "Terraza",
      icon: <TerraceIco fill={hasTerrace ? "#FFF" : "#3E3E3E"} />,
      checked: hasTerrace,
    },
  ]);

  const handleFeatureCheck = (id) => {
    setCheckboxes((prevCheckboxes) => {
      return prevCheckboxes.map((checkbox) => {
        if (checkbox.id === id) {
          return {
            ...checkbox,
            checked: !checkbox.checked,
            icon: getIcon(checkbox.checked ? "#F3E3E3EF" : "#FFF").filter(
              (feature) => feature.id == checkbox.id
            )[0].icon,
          };
        }
        return checkbox;
      });
    });
  };

  const uncheckAllFeatures = () => {
    setCheckboxes((prevCheckboxes) => {
      return prevCheckboxes.map((checkbox) => {
        return { ...checkbox, checked: false };
      });
    });
  };

  const RadioOptionsStates = {
    PartialReform: 2,
    ToReform: 5,
    GoodCondition: 1,
    BrandNew: 6,
  };
  const getOptionLabel = (option) => {
    switch (option) {
      case "PartialReform":
        return "Reforma parcial";
      case "ToReform":
        return "Para reformar";
      case "GoodCondition":
        return "Buen estado";
      case "BrandNew":
        return "A estrenar";
      default:
        return "";
    }
  };

  const handleBuildStatusChoice = (option) => {
    setBuildStatus(option === buildStatus ? null : option);
    setOptionError(""); // Limpiar el mensaje de error al seleccionar una opción
  };

  const handleTypeOfPropertyChange = (e) => {
    setTypeOfProperty(e.target.value);
    setErrorTypeOfProperty(""); // Limpiar el mensaje de error al cambiar el tipo de propiedad
  };

  const handleArea = (e) => {
    const areaRegex = /^[0-9]{0,10}$/;
    const inputValue = e.target.value;
    // Validar que solo se ingresen números
    if (areaRegex.test(inputValue)) {
      setAreaSelected(inputValue);
    }
    setAreaError("");
  };

  const handleFloorChange = (e) => {
    const value = e.target.value;
    setFloorNumber(value);
  };

  // By clicking on the "Aplicar" button
  const handleSubmit = () => {
    if (!typeOfPropertySelected) {
      setErrorTypeOfProperty("Seleccione un tipo de propiedad para continuar.");
      return;
    }
    if (!areaSelected) {
      setAreaError("Ingrese un área para continuar.");
      return;
    }
    if (!buildStatus) {
      setOptionError("Selecciona una opción para continuar.");
      return;
    }
    // If there are no errors, execute send()
    send();
  };

  // Send the data to the following view
  const send = () => {
    const features = checkboxes.reduce((acc, checkbox) => {
      acc[checkbox.id] = checkbox.checked ? 1 : 0;
      return acc;
    }, {});

    const queryParams = new URLSearchParams({
      address: address,
      block: block,
      staircase: staircase,
      lat: lat,
      lon: lon,
      property_type: typeOfPropertySelected,
      area: areaSelected,
      floor: floorNumber,
      build_status: buildStatus,
      rooms: roomsNumber,
      nbaths: bathroomNumber,
      boundary_id: boundary_id ? boundary_id : null,
      municipio: municipio,
      provincia: provincia,
      postal_code: postal_code,
      features: JSON.stringify(features),
      cadastral_bi: cadastral_bi,
    });
    navigate(
      `/dataRequest?key=${iframeKey}&${queryParams.toString()}${
        qaTesting ? "&qa_testing=true" : ""
      }`,
      { replace: true }
    );
    return window.location.reload();
  };

  useEffect(() => {
    if (area) {
      setAreaSelected(area);
    }
  }, [area]);

  const back = () => {
    navigate("/?key=" + iframeKey);
    return window.location.reload();
  };

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: `${MAPBOX_STYLE}`,
      center: [lon, lat],
      zoom: 17,
    });

    // add markers to map
    map.current.on("load", () => {
      // Add the default Mapbox red marker pin to the map style's sprite
      map.current.loadImage(
        mapPin, // URL to the Mapbox red pin marker
        (error, image) => {
          if (error) throw error;
          map.current.addImage("red-marker", image);
        }
      );

      // Add a layer using the default red marker icon
      map.current.addLayer({
        id: "marker-layer",
        type: "symbol",
        source: {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [lon, lat], // Coordinates for the marker
                },
                properties: {
                  iconSize: [30, 30],
                },
              },
              // Add more features if needed
            ],
          },
        },
        layout: {
          "icon-image": "red-marker", // Use the added red marker icon
          "icon-size": 1,
          "text-field": "{title}",
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "text-offset": [0, 1],
          "text-anchor": "top",
        },
      });
    });
  }, [lon, lat]);

  return (
    <main className="flex flex-col w-full font-roboto parent-container">
      <nav className="desktop:flex hidden items-center w-full">
        <button onClick={back} className="flex items-center space-x-2">
          <ArrowLeft />
          <p className="text-[#262626] hover:text-[#2a2a2a]">
            Atrás /{"\u00A0"}
          </p>
        </button>
        <p className="text-[#747D93]">{`${normalizeAddress(address)}${
          !Number.isNaN(block) ? `Bloque ${block},` : ""
        }${
          staircase !== "undefined" && staircase !== "null"
            ? `, Escalera ${staircase}`
            : ""
        }`}</p>
      </nav>
      <div className="flex w-full justify-center">
        {/* Left */}
        <article className="hidden desktop:flex flex-col w-1/2 pt-4 space-y-2">
          <h2 className="text-[#212632]">Localización</h2>
          <h3
            className="text-[#212632]"
            style={{ fontSize: "1.5rem" }}
          >{`${normalizeAddress(address)}${
            !Number.isNaN(block) ? `, Bloque ${block},` : ""
          }${
            staircase !== "undefined" && staircase !== "null"
              ? `, Escalera ${staircase}`
              : ""
          }`}</h3>
          {/* Map MAPBOX */}
          <div className="relative h-full">
            <div
              className="w-full h-full border-none rounded-2xl"
              ref={mapContainer}
              id="iframeMapBox"
            ></div>
            <div className="rounded-lg bg-white absolute bottom-14 left-4 shadow">
              {/* CON STREET_VIEW_API */}
              <iframe
                title="Google Street View Preview"
                src={`https://www.google.com/maps/embed/v1/streetview?key=${GOOGLE_MAPS_API_KEY}&location=${lat},${lon}&fov=60&source=outdoor&radius=40`}
                className="rounded-lg w-60 h-60"
                allowFullScreen=""
                aria-hidden="false"
                tabIndex="0"
              />
              {/* CON IMAGEN SOLO */}
              {/* <img
                className="rounded-lg w-32 h-28"
                src={streetViewUrl}
                alt="Google Street Map"
              /> */}
              <span className="flex flex-wrap space-x-1 text-xs text-[#747D93] pt-2">
                <p>{`${lat},`}</p>
                <p>{`${lon}`}</p>
              </span>
            </div>
          </div>
        </article>

        {/* Right */}
        <form className="flex flex-col desktop:w-1/2 laptop:w-2/3 w-full tablet:px-[51px] px-5 desktop:py-0 py-4 justify-between">
          <div>
            <div className="w-full">
              <h1 className="text-[#212632] tablet:text-4xl text-3xl font-bold">
                Perfila tu activo
              </h1>
              <div className="flex py-2">
                <p className="w-full">
                  Para que la valoración sea más precisa, por favor, rellena
                  todos los campos posibles. El catastro te ayudará a
                  autocompletar los que no perfiles.
                </p>
              </div>
            </div>

            <div className="py-8 space-y-6">
              {/* <div className="py-12 space-y-6 px-2 overflow-y-auto h-[200px]"> */}
              <div className="flex w-full justify-between space-x-6">
                <div className="w-full space-y-3">
                  <h2>
                    <strong>Inmueble</strong>
                  </h2>
                  <select
                    id="TypeOfProperty"
                    className="w-full border rounded-lg px-[14px] py-[16px]"
                    onChange={handleTypeOfPropertyChange}
                    value={typeOfProperty}
                  >
                    <option value="" disabled>
                      Seleccione una opción
                    </option>
                    <option value={1}>Ático</option>
                    <option value={4}>Piso</option>
                    <option value={2}>Duplex</option>
                    <option value={3}>Chalet</option>
                    <option value={5}>Chalet pareado</option>
                    <option value={6}>Chalet adosado</option>
                    <option value={7}>Estudio</option>
                    <option value={8}>Casa rústica</option>
                  </select>
                  {/* Error message */}
                  {errorTypeOfProperty && (
                    <p className="text-[#DE0E48] pt-1">{errorTypeOfProperty}</p>
                  )}
                </div>
                <div className="w-full space-y-3">
                  <h2>
                    <strong>Área</strong>
                  </h2>
                  <div style={{ position: "relative" }}>
                    <span
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "14px",
                        transform: "translateY(-50%)",
                        color: "#000",
                      }}
                    >
                      m²
                    </span>
                    <input
                      type="number"
                      onChange={handleArea}
                      value={areaSelected === null ? "" : areaSelected}
                      className="bg-[#EFF2F7] w-full rounded-lg pl-[10px] pr-[40px] py-[16px] text-right" // Ajusta el padding según sea necesario
                    />
                  </div>
                  {/* Error message */}
                  {areaError && (
                    <p className="text-[#DE0E48] pt-1">{areaError}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col w-full justify-between space-y-3">
                <h2>
                  <strong>Planta</strong>
                </h2>
                <div className="flex w-full mobile:flex-nowrap flex-wrap items-center justify-between space-x-6 mobile:space-y-0 space-y-2">
                  <div className="w-full">
                    <div className="flex items-center space-x-2">
                      <PlantOfInterest />
                      <p className="profiling-stays-label">Planta</p>
                    </div>
                  </div>
                  <div className="flex items-center mobile:justify-end justify-center w-full ">
                    <div className="flex space-x-3">
                      <ExcentIncrementButton
                        type="button"
                        onClick={decrementFloor}
                      >
                        -
                      </ExcentIncrementButton>
                      <input
                        type={isFloorFocused ? "number" : "text"}
                        onFocus={() => setIsFloorFocused(true)}
                        onBlur={() => {
                          if (floorNumber === "") {
                            setFloorNumber(null);
                          }
                          setIsFloorFocused(false);
                        }}
                        value={floorNumber === null ? "-" : floorNumber}
                        className="flex items-center justify-center bg-[#EFF2F7] rounded-lg w-14 text-center asset-property-number-input"
                        onChange={(e) => handleFloorChange(e)}
                      />
                      <ExcentIncrementButton
                        type="button"
                        onClick={incrementFloor}
                      >
                        +
                      </ExcentIncrementButton>
                    </div>
                  </div>
                </div>
              </div>
              {/* State */}
              <div className="flex flex-col w-full space-y-3">
                <h2>
                  <strong>Estado</strong>
                </h2>
                {/* Options */}
                <div className="flex flex-wrap w-full">
                  {Object.keys(RadioOptionsStates).map((option) => (
                    <label key={option} className="mr-3 pb-2">
                      <input
                        type="radio"
                        name="option"
                        className="hidden"
                        checked={buildStatus === option}
                        onChange={() =>
                          handleBuildStatusChoice(RadioOptionsStates[option])
                        }
                      />
                      <ExcentButton
                        type="button"
                        checked={buildStatus === RadioOptionsStates[option]}
                        onClick={() =>
                          handleBuildStatusChoice(RadioOptionsStates[option])
                        }
                      >
                        {getOptionLabel(option)}
                      </ExcentButton>
                    </label>
                  ))}
                </div>

                {/* Error message */}
                {optionError && (
                  <p className="text-[#DE0E48] pt-1">{optionError}</p>
                )}
              </div>

              {/* Stays */}
              <div className="flex flex-col w-full justify-between space-y-3">
                <h2>
                  <strong>Estancias</strong>
                </h2>
                {/* Rooms quantity selector */}
                <div className="flex mobile:flex-nowrap flex-wrap w-full items-center justify-between space-x-6 mobile:space-y-0 space-y-2">
                  <div className="w-full">
                    <div className="flex items-center space-x-2">
                      <Rooms />
                      <p className="profiling-stays-label">Habitaciones</p>
                    </div>
                  </div>
                  <div className="flex items-center mobile:justify-end justify-center w-full ">
                    <div className="flex space-x-3">
                      <ExcentIncrementButton
                        type="button"
                        onClick={() => {
                          decrementRooms();
                        }}
                      >
                        -
                      </ExcentIncrementButton>
                      <input
                        type={isRoomsFocused ? "number" : "text"}
                        onFocus={() => setIsRoomsFocused(true)}
                        onBlur={() => {
                          setIsRoomsFocused(false);
                          if (roomsNumber === "") {
                            setRoomsNumber("-");
                          } else {
                            setRoomsNumber(parseInt(roomsNumber));
                          }
                        }}
                        value={roomsNumber === null ? "-" : roomsNumber}
                        className="flex items-center justify-center bg-[#EFF2F7] rounded-lg w-14 text-center asset-property-number-input"
                        onChange={(e) => {
                          setRoomsNumber(e.target.value);
                        }}
                      />
                      <ExcentIncrementButton
                        type="button"
                        onClick={() => {
                          incrementRooms();
                        }}
                      >
                        +
                      </ExcentIncrementButton>
                    </div>
                  </div>
                </div>
                {/* Baths quantity selector */}
                <div className="flex mobile:flex-nowrap flex-wrap w-full items-center justify-between space-x-6">
                  <div className="w-full">
                    <div className="flex items-center space-x-2">
                      <Bathrooms />
                      <p className="profiling-stays-label">Baños</p>
                    </div>
                  </div>
                  <div className="flex items-center mobile:justify-end justify-center w-full ">
                    <div className="flex space-x-3">
                      <ExcentIncrementButton
                        type="button"
                        onClick={() => {
                          decrementBathroom();
                        }}
                      >
                        -
                      </ExcentIncrementButton>
                      <div className="flex items-center justify-center bg-[#EFF2F7] rounded-lg w-14">
                        <input
                          type={isBathroomsFocused ? "number" : "text"}
                          onFocus={() => setIsBathroomsFocused(true)}
                          onBlur={() => {
                            setIsBathroomsFocused(false);
                            if (bathroomNumber === "") {
                              setBathroomNumber("-");
                            } else {
                              setBathroomNumber(parseInt(bathroomNumber));
                            }
                          }}
                          value={bathroomNumber === null ? "-" : bathroomNumber}
                          className="flex items-center justify-center bg-[#EFF2F7] rounded-lg w-14 text-center asset-property-number-input"
                          onChange={(e) => {
                            setBathroomNumber(e.target.value);
                          }}
                        />
                      </div>
                      <ExcentIncrementButton
                        type="button"
                        onClick={() => {
                          incrementBathroom();
                        }}
                      >
                        +
                      </ExcentIncrementButton>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-col w-full space-y-3">
                  <h2>
                    <strong>Características</strong>
                  </h2>
                  {/* Features options */}
                  <ul className="flex flex-wrap w-full">
                    {checkboxes.map((checkbox) => (
                      <label key={checkbox.id} className="mr-3 pb-2">
                        <input
                          type="checkbox"
                          name={checkbox.id}
                          className="hidden"
                          checked={checkbox.checked}
                        />
                        <ExcentButton
                          type="button"
                          checked={checkbox.checked}
                          onClick={() => handleFeatureCheck(checkbox.id)}
                        >
                          {checkbox.icon}
                          <span>{checkbox.label}</span>
                        </ExcentButton>
                      </label>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="flex tablet:flex-row flex-col items-center justify-center w-full tablet:space-x-6 space-x-0 tablet:space-y-0 space-y-4">
            <ExcentSubmitButton1
              type="button"
              className="border border-[#DE0E48] hover:border-[#9E0A33] rounded-full rounded-lg tablet:w-60 w-full py-3 profiling-button"
              onClick={() => resetFilters()}
              width={"300"}
            >
              Quitar filtros
            </ExcentSubmitButton1>
            <ExcentSubmitButton2 type="button" onClick={handleSubmit}>
              <span className="text-white">Aplicar</span>
            </ExcentSubmitButton2>
          </div>
        </form>
      </div>
      <Footer />
    </main>
  );
};

export default ActiveProfiling;
